import { useCallback, useMemo, useState } from 'react'

type TapBpmResult = {
  bpm: number | null
  stable: boolean
}

const calculateTapBpm = (tapTimes: number[]): TapBpmResult => {
  if (tapTimes.length < 4) {
    return { bpm: null, stable: false }
  }

  const intervals = tapTimes
    .slice(1)
    .map((time, index) => time - tapTimes[index]!)
    .filter((interval) => interval >= 250 && interval <= 2000)

  if (intervals.length < 3) {
    return { bpm: null, stable: false }
  }

  const sorted = [...intervals].sort((left, right) => left - right)
  const median = sorted[Math.floor(sorted.length / 2)]!
  const filtered = intervals.filter((interval) => Math.abs(interval - median) / median <= 0.25)

  if (filtered.length < 3) {
    return { bpm: null, stable: false }
  }

  const average = filtered.reduce((sum, interval) => sum + interval, 0) / filtered.length
  const variance = filtered.reduce((sum, interval) => sum + ((interval - average) ** 2), 0) / filtered.length
  const deviation = Math.sqrt(variance)
  const bpm = 60000 / average

  return {
    bpm: Math.round(bpm),
    stable: deviation / average <= 0.18 && bpm >= 40 && bpm <= 220
  }
}

export const useTapBpm = () => {
  const [tapTimes, setTapTimes] = useState<number[]>([])
  const tapBpm = useMemo(() => calculateTapBpm(tapTimes), [tapTimes])

  const tapBpmBeat = useCallback((): void => {
    const now = performance.now()

    setTapTimes((current) => {
      const next = current.length > 0 && now - current[current.length - 1]! > 3000
        ? [now]
        : [...current, now]

      return next.slice(-16)
    })
  }, [])

  const resetTapBpm = useCallback((): void => {
    setTapTimes([])
  }, [])

  return {
    resetTapBpm,
    tapBpm,
    tapBpmBeat,
    tapCount: tapTimes.length
  }
}
